// Upload helper with real-time progress and automatic video-poster extraction.
//
// Usage:
//   const { url, kind, poster_url } = await uploadWithProgress(file, token, (p) => {
//     // p = { percent, loaded, total, phase }
//   });
//
// Phases: 'compressing' | 'poster' | 'uploading' | 'done'
//
// Videos are processed client-side to extract a JPG poster frame which is sent
// as a `poster` sidecar on the same multipart request. This means the backend
// can serve a preview thumbnail even before the video is fully downloaded.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VIDEO_MIME_RE = /^video\//i;

// Images that we can safely re-encode through a <canvas>. HEIC/AVIF/SVG/GIF
// are intentionally excluded because canvas re-encoding loses animation
// (GIF), transparency in unpredictable ways (SVG), or fails to decode at all
// (HEIC on most desktop browsers). Those formats pass through untouched.
const COMPRESSIBLE_IMAGE_MIME_RE = /^image\/(jpeg|jpg|pjpeg|png|webp)$/i;
const COMPRESSIBLE_IMAGE_NAME_RE = /\.(jpe?g|png|webp)$/i;

// Threshold above which we resize images client-side. Cloudflare's free tier
// drops bodies > 100MB and times out at 100s, so anything larger than ~4MB
// risks a 520 from a slow uplink. We resize aggressively but only for images.
const IMAGE_COMPRESS_THRESHOLD_BYTES = 4 * 1024 * 1024; // 4 MB
const IMAGE_MAX_DIMENSION = 2560; // px, longest edge
const IMAGE_JPEG_QUALITY = 0.85;

// Decode a File into an HTMLImageElement via ObjectURL. Resolves to null on
// failure so callers can fall back to the original file.
function loadImageFromFile(file) {
  return new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        // Note: don't revoke URL until after draw, but Image holds its own
        // decoded copy, so we can revoke immediately.
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
        resolve(img);
      };
      img.onerror = () => {
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
        resolve(null);
      };
      img.src = url;
    } catch {
      resolve(null);
    }
  });
}

// Re-encode an oversized image at most IMAGE_MAX_DIMENSION on the longest
// edge, as a quality-0.85 JPEG. Returns the original File untouched if the
// image is already small enough or if anything goes wrong (silent fallback).
export async function compressImageIfNeeded(file, opts = {}) {
  if (!file) return file;
  const {
    threshold = IMAGE_COMPRESS_THRESHOLD_BYTES,
    maxDimension = IMAGE_MAX_DIMENSION,
    quality = IMAGE_JPEG_QUALITY,
  } = opts;

  const isCompressible =
    COMPRESSIBLE_IMAGE_MIME_RE.test(file.type || '') ||
    (!file.type && COMPRESSIBLE_IMAGE_NAME_RE.test(file.name || ''));
  if (!isCompressible) return file;
  if (file.size <= threshold) return file;

  const img = await loadImageFromFile(file);
  if (!img) return file;

  const { naturalWidth: w, naturalHeight: h } = img;
  if (!w || !h) return file;

  const scale = Math.min(1, maxDimension / Math.max(w, h));
  const targetW = Math.max(1, Math.round(w * scale));
  const targetH = Math.max(1, Math.round(h * scale));

  try {
    const canvas = document.createElement('canvas');
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext('2d');
    // White background so PNG transparency doesn't go black when we encode
    // to JPEG (which has no alpha channel).
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, targetW, targetH);
    ctx.drawImage(img, 0, 0, targetW, targetH);

    const blob = await new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b), 'image/jpeg', quality);
    });
    if (!blob) return file;
    // Only swap if the new blob is actually smaller; otherwise the original
    // was already well-compressed (e.g. a tightly-encoded photo).
    if (blob.size >= file.size) return file;

    const baseName = (file.name || 'image').replace(/\.[^.]+$/, '');
    return new File([blob], `${baseName}.jpg`, { type: 'image/jpeg', lastModified: Date.now() });
  } catch {
    return file;
  }
}

export function formatBytes(bytes) {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

// Extract a JPEG poster frame from a video File. Resolves to a Blob, or null
// if the frame couldn't be captured (silent fallback - the video will still
// upload, but without a sidecar poster).
export function extractVideoPoster(file, { seekRatio = 0.1, maxWidth = 1280, quality = 0.82 } = {}) {
  return new Promise((resolve) => {
    try {
      const url = URL.createObjectURL(file);
      const video = document.createElement('video');
      video.preload = 'metadata';
      video.muted = true;
      video.playsInline = true;
      video.crossOrigin = 'anonymous';
      video.src = url;

      let done = false;
      const cleanup = () => {
        try { URL.revokeObjectURL(url); } catch { /* noop */ }
        video.removeAttribute('src');
        try { video.load(); } catch { /* noop */ }
      };
      const finish = (blob) => {
        if (done) return;
        done = true;
        cleanup();
        resolve(blob || null);
      };

      const bail = () => finish(null);
      // Hard timeout in case events never fire
      const timeout = setTimeout(bail, 15000);

      video.addEventListener('error', bail, { once: true });

      video.addEventListener('loadedmetadata', () => {
        const duration = Number.isFinite(video.duration) ? video.duration : 0;
        const target = duration > 0 ? Math.min(duration * seekRatio, 2.5) : 0;
        // Small delay helps Safari actually decode the first frame
        try { video.currentTime = target; } catch { bail(); }
      }, { once: true });

      video.addEventListener('seeked', () => {
        try {
          const vw = video.videoWidth;
          const vh = video.videoHeight;
          if (!vw || !vh) { bail(); return; }
          const scale = Math.min(1, maxWidth / vw);
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(vw * scale);
          canvas.height = Math.round(vh * scale);
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(
            (blob) => {
              clearTimeout(timeout);
              finish(blob);
            },
            'image/jpeg',
            quality
          );
        } catch {
          clearTimeout(timeout);
          bail();
        }
      }, { once: true });
    } catch {
      resolve(null);
    }
  });
}

// Low-level XHR POST with upload progress. Returns parsed JSON.
function xhrUpload(url, formData, token, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.withCredentials = true;
    if (token) xhr.setRequestHeader('Authorization', `Bearer ${token}`);

    xhr.upload.onprogress = (e) => {
      if (!onProgress) return;
      if (e.lengthComputable) {
        onProgress({
          percent: Math.round((e.loaded / e.total) * 100),
          loaded: e.loaded,
          total: e.total,
          phase: 'uploading',
        });
      } else {
        onProgress({ percent: null, loaded: e.loaded, total: null, phase: 'uploading' });
      }
    };

    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.onabort = () => reject(new Error('Upload aborted'));
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          resolve(JSON.parse(xhr.responseText));
        } catch (e) {
          reject(new Error('Invalid JSON response from upload'));
        }
      } else {
        let detail = `${xhr.status} ${xhr.statusText}`;
        try {
          const body = JSON.parse(xhr.responseText);
          if (body?.detail) detail = body.detail;
        } catch { /* noop */ }
        reject(new Error(detail));
      }
    };

    xhr.send(formData);
  });
}

// Main entry point. Handles both images and videos.
// `extraFormEntries` lets callers add extra fields to the multipart POST
// (e.g. { keep_raw: 'true' } for raw PDF/DWG preservation on the server).
//
// PDFs upload as raw PDF by default (so the gallery can show them as a
// PDF tile). Callers that explicitly want first-page conversion to PNG
// (e.g. SlidesManager when uploading a map background) should pass
// `extraFormEntries: { keep_raw: 'false' }`.
const PDF_MIME_RE = /^application\/pdf$/i;
const PDF_NAME_RE = /\.pdf$/i;

export async function uploadWithProgress(file, token, onProgress, extraFormEntries = {}) {
  const isVideo = VIDEO_MIME_RE.test(file.type || '');
  const isPdf = PDF_MIME_RE.test(file.type || '') || PDF_NAME_RE.test(file.name || '');

  // Compress oversized images client-side BEFORE the upload starts so we
  // never blow past Cloudflare's body-size / 100s timeout limits. Videos
  // and PDFs pass through untouched.
  let outFile = file;
  if (!isVideo && !isPdf) {
    onProgress?.({ percent: 0, loaded: 0, total: file.size, phase: 'compressing' });
    outFile = await compressImageIfNeeded(file);
  }

  let posterBlob = null;
  if (isVideo) {
    onProgress?.({ percent: 0, loaded: 0, total: outFile.size, phase: 'poster' });
    posterBlob = await extractVideoPoster(outFile);
  }

  const fd = new FormData();
  fd.append('file', outFile);
  if (posterBlob) fd.append('poster', posterBlob, 'poster.jpg');
  // Default: PDFs preserve as PDF so galleries get a real .pdf URL back.
  // Caller can override by passing extraFormEntries.keep_raw explicitly.
  const merged = { ...(isPdf ? { keep_raw: 'true' } : {}), ...(extraFormEntries || {}) };
  for (const [key, value] of Object.entries(merged)) {
    if (value != null) fd.append(key, String(value));
  }

  // Immediately signal upload start at 0%
  onProgress?.({ percent: 0, loaded: 0, total: outFile.size, phase: 'uploading' });

  const data = await xhrUpload(`${API}/admin/upload`, fd, token, onProgress);

  onProgress?.({ percent: 100, loaded: outFile.size, total: outFile.size, phase: 'done' });
  return data;
}
